import { BookOutlined } from "@ant-design/icons"
import { capitalize } from "lodash"
import {
  Breadcrumb,
  Col,
  Form,
  Layout,
  notification,
  Row,
  Spin,
  Typography
} from "antd"
import { AxiosError } from "axios"
import { LayoutPageContent, LayoutPageHeader } from "components"
import usePlatform from "contexts/platform/use-platform"
import { TErrorState } from "models/errors"
import { ICreateCompanyRequest, IDisplayCompany } from "platform-client/types"
import React from "react"
import { useMutation, useQuery } from "react-query"
import { RouteComponentProps, useHistory, withRouter } from "react-router"
import { Link } from "react-router-dom"
import { BrokerForm } from "./BrokerForm"

const CreateBroker: React.FC<CreateBrokerProps> = (props) => {
  const { id: brokerId, subpage } = props.match.params as {
    id?: string
    subpage: string
  }
  const [form] = Form.useForm()
  const platform = usePlatform()
  const history = useHistory()
  const [apiError, setApiError] = React.useState<TErrorState>()

  const getBroker = useQuery(
    ["getBroker", brokerId],
    () => platform.company.getCompany(brokerId as string),
    {
      enabled: subpage === "edit" && !!brokerId,
      onSuccess: (data) => {
        const getStatusValue = (status: number | string | undefined | null) => {
          if (status === undefined || status === null) return "Enabled"
          if (status === 0 || status === "Enabled") return "Enabled"
          if (status === 1 || status === "Disabled") return "Disabled"
          return "Enabled"
        }

        form.setFieldsValue({
          name: data?.name,
          companyType: data?.companyType,
          firstName: data?.contactName?.firstName,
          lastName: data?.contactName?.lastName,
          emailAddress: data?.contactEmailAddress,
          phoneNumber: data?.contactPhoneNumber,
          uniqueReference: data?.address?.uniqueReference,
          address: data?.address
            ? {
                ...data.address,
                country: JSON.stringify(data.address?.country)
              }
            : undefined,
          enabledProducts: (data?.enabledProducts || []).map(
            (item) => item.referenceID
          ),
          abn: data?.abn,
          afsl: data?.afsl,
          status: getStatusValue(data?.status),
          parentCompanyID: data?.parentCompanyID
        })
      }
    }
  )

  const getParentCompany = useQuery(
    ["getParentCompany", getBroker.data?.parentCompanyID],
    () =>
      platform.company.getCompany(getBroker.data?.parentCompanyID as string),
    {
      enabled: subpage === "edit" && !!getBroker.data?.parentCompanyID
    }
  )

  const createBroker = useMutation(
    (request: ICreateCompanyRequest) => platform.company.createCompany(request),
    {
      onError: (error: AxiosError) => {
        setApiError({
          errorNumber: error.code ? parseInt(error.code) : 500,
          validationMessage: "Unable to create broker, please try again."
        })
      },
      onSuccess: (response) => {
        notification.success({
          message: "Broker created successfully. Redirecting you..."
        })
        history.push(`/broker/${response.id}/`)
      }
    }
  )

  const updateBroker = useMutation(
    (request: ICreateCompanyRequest) =>
      platform.company.updateCompany(brokerId as string, request),
    {
      onError: (error: AxiosError) => {
        setApiError({
          errorNumber: error.code ? parseInt(error.code) : 500,
          validationMessage: "Unable to update broker, please try again."
        })
      },
      onSuccess: (response) => {
        notification.success({
          message: "Broker updated successfully. Redirecting you..."
        })
        history.push(`/broker/${response.id}/`)
      }
    }
  )

  const onFinish = (formData: ICreateBrokerForm) => {
    const requestBody: ICreateCompanyRequest = {
      name: formData.name,
      companyType: "Broker",
      brokerCanBindBusiness: false,
      contactName: {
        title: null,
        firstName: formData.firstName || null,
        lastName: formData.lastName || null
      },
      contactEmailAddress: formData.emailAddress || null,
      contactPhoneNumber: formData.phoneNumber || null,
      address: {
        line1: formData?.address?.line1 || null,
        line2: formData?.address?.line2 || null,
        line3: formData?.address?.line3 || null,
        locality: formData?.address?.locality || null,
        province: formData?.address?.province || null,
        postalCode: formData?.address?.postalCode || null,
        provinceReferenceID: formData?.address?.provinceReferenceID,
        uniqueReference: formData?.address?.uniqueReference || null,
        country: formData?.address?.country
          ? JSON.parse(formData?.address?.country)
          : { referenceID: null, text: null }
      },
      enabledProducts: formData.enabledProducts,
      abn: formData.abn || null,
      afsl: formData.afsl || null,
      status: formData.status || "Enabled",
      parentCompanyID: formData.parentCompanyID || null
    }

    if (subpage === "edit") {
      updateBroker.mutate(requestBody)
    }

    if (subpage === "create") {
      createBroker.mutate(requestBody)
    }
  }

  return (
    <Layout>
      <LayoutPageHeader
        ghost={false}
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item key="/brokers">
              <Link to="/brokers">
                <BookOutlined />
                &nbsp;Brokers
              </Link>
            </Breadcrumb.Item>
            {subpage === "edit" && (
              <Breadcrumb.Item>
                <Link to={`/broker/${brokerId}`}>
                  {getBroker.data?.referenceID || getBroker.data?.name || ""}
                </Link>
              </Breadcrumb.Item>
            )}
            <Breadcrumb.Item>{capitalize(subpage)}</Breadcrumb.Item>
          </Breadcrumb>
        }
        title={subpage === "create" ? "Create Broker" : "Edit Broker"}
        style={{ marginBottom: 20 }}
      />
      <LayoutPageContent error={apiError}>
        <Spin
          spinning={
            getBroker.isLoading ||
            (subpage === "edit" && getParentCompany.isLoading)
          }
          tip="Loading broker data..."
        >
          <Row>
            <Col span={12} offset={6} style={{ padding: "50px 15px 15px" }}>
              <Typography.Title level={3}>
                {subpage === "create"
                  ? "Create Broker"
                  : `Editing Broker: ${getBroker.data?.name || ""}`}
              </Typography.Title>
              <Typography.Paragraph>
                Please complete the form below to create a broker. You will be
                redirected to the summary page on creation.
              </Typography.Paragraph>
              <BrokerForm
                form={form}
                onFinish={onFinish}
                isLoading={
                  getBroker.isLoading ||
                  (subpage === "edit" && getParentCompany.isLoading)
                }
                submitButtonLoading={
                  createBroker.isLoading || updateBroker.isLoading
                }
                parentCompanyDisabled={
                  subpage === "edit" || !!getBroker.data?.parentCompanyID
                }
                enabledProductsDisabled={
                  subpage === "edit" && !!getBroker.data?.parentCompanyID
                }
                parentCompany={
                  getBroker.data?.parentCompanyID && getParentCompany.data
                    ? getParentCompany.data
                    : getBroker.data?.parentCompanyID
                    ? ({
                        id: getBroker.data.parentCompanyID
                      } as IDisplayCompany)
                    : undefined
                }
              />
            </Col>
          </Row>
        </Spin>
      </LayoutPageContent>
    </Layout>
  )
}

interface ICreateBrokerForm {
  emailAddress?: string
  enabledProducts?: string[]
  address?: {
    line1?: string
    line2?: string
    line3?: string
    locality?: string
    postalCode?: string
    province?: string
    country?: string
    provinceReferenceID?: string
    uniqueReference?: string
  }
  firstName?: string
  lastName?: string
  name: string
  phoneNumber?: string
  abn?: string
  afsl?: string
  status?: "Enabled" | "Disabled"
  parentCompanyID?: string
}

type CreateBrokerProps = RouteComponentProps

export default withRouter(CreateBroker)
